// material
import React from 'react';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import { Card, Box, Typography, Stack, Paper, Skeleton } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TramOutlinedIcon from '@mui/icons-material/TramOutlined';
import ElectricRickshawOutlinedIcon from '@mui/icons-material/ElectricRickshawOutlined';
import SailingOutlinedIcon from '@mui/icons-material/SailingOutlined';
import { URL_CONSTANT } from '../../../URLConstants';

// ----------------------------------------------------------------------

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#F9F9FF',
  ...theme.typography.body2,
  textAlign: 'center',
  borderRadius: '12px',
  minWidth: 'max-content',
  maxWidth: '100px',
  cursor: 'pointer',
  padding: theme.breakpoints.down('md') ? '8px' : '16px', // Responsive padding
  height: theme.breakpoints.down('md') ? '60px' : '70px' // Responsive height
}));

const useStyles = makeStyles((theme) => ({
  customDashCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '20px',
    minHeight: '190px',
    padding: '15px',
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '20px',
    alignItems: 'center',

    // Media query for small screens (minWidth: 'sm')
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column'
    }
  }
}));

// ----------------------------------------------------------------------

let TOTAL = 0;

if (process.env.REACT_APP_ENVIRONMENT === 'development') TOTAL = 800;

export default function SmartLockDevices({ dashboardData, loader }) {
  const classes = useStyles();
  const navigate = useNavigate();

  const vehicleIcons = (type) => {
    switch (type) {
      case 'TRUCK':
        return <LocalShippingIcon sx={{ color: '#171D40', fontSize: { xs: 18, sm: 24 } }} />;
      case 'TRAIN':
        return <TramOutlinedIcon sx={{ color: '#171D40', fontSize: { xs: 18, sm: 24 } }} />;
      case 'SHIP':
        return <SailingOutlinedIcon sx={{ color: '#171D40', fontSize: { xs: 18, sm: 24 } }} />;
      case 'CONTAINER':
        return <LocalShippingIcon sx={{ color: '#171D40', fontSize: { xs: 18, sm: 24 } }} />;
      case 'TEMPO':
        return (
          <ElectricRickshawOutlinedIcon sx={{ color: '#171D40', fontSize: { xs: 18, sm: 24 } }} />
        );
      case 'OTHER':
        return <TramOutlinedIcon sx={{ color: '#171D40', fontSize: { xs: 18, sm: 24 } }} />;
      default:
        break;
    }
  };

  const gotoDevicePage = (deviceStatus) => {
    navigate(URL_CONSTANT.DEVICE, { state: { status: deviceStatus } });
  };

  const goToVehicle = (vehicleType) => {
    if (vehicleType) {
      navigate(URL_CONSTANT.VEHICLE, { state: { type: vehicleType } });
    }
  };

  return (
    <Card data-testid="smartlock-devices" className={classes.customDashCard}>
      {loader ? (
        <Skeleton variant="rectangular" width="100px" height="100px" />
      ) : (
        <Stack alignItems="center" justifyContent="center">
          <Typography
            sx={(theme) => ({
              fontWeight: 900,
              color: '#171D40',
              fontSize: '43px',
              lineHeight: '54px',
              cursor: 'pointer',

              // Media query for small screens (down from 'sm')
              [theme.breakpoints.down('sm')]: {
                fontSize: '32px',
                lineHeight: '40px'
              }
            })}
            onClick={() => gotoDevicePage('')}
          >
            {dashboardData?.devices?.total !== undefined && dashboardData?.devices?.total !== null
              ? dashboardData?.devices?.total
              : '---'}
          </Typography>

          <Typography
            sx={(theme) => ({
              fontWeight: 700,
              color: '#171D40',
              fontSize: '16px',
              lineHeight: '25px',
              [theme.breakpoints.down('sm')]: {
                fontSize: '14px',
                lineHeight: '18px'
              }
            })}
          >
            Devices
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#0DA626',
              fontSize: '12px',
              lineHeight: '20px',
              cursor: 'pointer'
            }}
            onClick={() => gotoDevicePage('online')}
          >
            Online:{' '}
            <span style={{ color: '#171D40', fontWeight: 700 }}>
              {
                dashboardData?.deviceData?.filter(
                  (item) => item?.deviceLiveStatus?.toUpperCase() === 'ONLINE'
                )?.length
              }
            </span>
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#C30A0A',
              fontSize: '12px',
              lineHeight: '20px',
              cursor: 'pointer'
            }}
            onClick={() => gotoDevicePage('offline')}
          >
            Offline:{' '}
            <span style={{ color: '#171D40', fontWeight: 700 }}>
              {
                dashboardData?.deviceData?.filter(
                  (item) => item?.deviceLiveStatus?.toUpperCase() === 'OFFLINE'
                )?.length
              }
            </span>
          </Typography>
        </Stack>
      )}

      <Box display="flex" gap="10px" flexWrap="wrap">
        {loader
          ? Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} height="50px" width="80px" variant="rectangular" />
            ))
          : dashboardData?.vehicles?.stats?.map((vehicle) => (
              <Item onClick={() => goToVehicle(vehicle?.type)} key={vehicle?.type}>
                {vehicleIcons(vehicle?.type?.toUpperCase())}
                <Typography
                  sx={(theme) => ({
                    fontWeight: 700,
                    color: '#535353',
                    fontSize: '14px',
                    lineHeight: '18px',
                    textTransform: 'capitalize',
                    [theme.breakpoints.down('sm')]: {
                      fontSize: '12px',
                      lineHeight: '14px'
                    }
                  })}
                >
                  {vehicle.count} {vehicle.type}
                </Typography>
              </Item>
            ))}
      </Box>
    </Card>
  );
}
